import { GenericAbortSignal } from 'axios';

import { DischargeReason, DischargeReasonOptions } from 'models/flexible/DischargeReason';
import { Paginated } from 'models/flexible/Paginated';
import type {
  AdminDischargeReasonCreateParams,
  AdminDischargeReasonIndexFields,
  AdminDischargeReasonIndexInclude,
  AdminDischargeReasonShowFields,
  AdminDischargeReasonShowInclude,
  AdminDischargeReasonUpdateParams,
} from 'services/api/fields';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const adminDischargeReasonsQueryKeys = {
  index: ['adminDischargeReasons'] as const,
  show: (id: string) => ['adminDischargeReasons', id] as const,
};

const URL = 'admin/discharge_reasons';

// ---------------------------------------------------------------------------
// Index
// ---------------------------------------------------------------------------

export type AdminIndexParams = BaseIndexQueryParams & {
  dischargedToGroupType?: string;
  groupType?: string;
};

export function adminIndexDischargeReasons<I extends AdminDischargeReasonIndexInclude = never>(
  params: AdminIndexParams,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Paginated<DischargeReason<AdminDischargeReasonIndexFields<I>>>> {
  return http
    .get(URL, {
      params: { ...params, ...(includes?.length ? { include: includes.join(',') } : {}) },
      signal,
    })
    .then((res) => res.data) as Promise<Paginated<DischargeReason<AdminDischargeReasonIndexFields<I>>>>;
}

// ---------------------------------------------------------------------------
// Show
// ---------------------------------------------------------------------------

export function adminShowDischargeReason<I extends AdminDischargeReasonShowInclude = never>(
  id: string,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<DischargeReason<AdminDischargeReasonShowFields<I>>> {
  return http
    .get(`${URL}/${id}`, {
      params: { include: includes?.join(',') },
      signal,
    })
    .then((res) => res.data) as Promise<DischargeReason<AdminDischargeReasonShowFields<I>>>;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export async function adminCreateDischargeReason(params: AdminDischargeReasonCreateParams) {
  return http.post<DischargeReasonOptions>(URL, params).then((res) => new DischargeReason(res.data));
}

export async function adminUpdateDischargeReason(id: string, params: AdminDischargeReasonUpdateParams) {
  return http.patch<DischargeReasonOptions>(`${URL}/${id}`, params).then((res) => new DischargeReason(res.data));
}

export async function adminArchiveDischargeReason(id: string) {
  return http.patch<{ data: DischargeReasonOptions }>(`${URL}/${id}`, { archived: true });
}
